import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown,
  Building2,
  CheckCircle2,
  Clock,
  Search,
  AlertCircle,
  FileText,
  Loader2,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from 'lib/utils';
import { useCompany } from 'contexts/CompanyContext';
import { ROUTES } from 'constants/routes';

// Status badge configurations
// Using design system colors: primary (green), secondary (blue), amber, orange, neutral
const STATUS_CONFIG = {
  delivered: {
    icon: CheckCircle2,
    color: 'text-primary-600 dark:text-primary-400',
    bg: 'bg-primary-500/10',
    label: 'empresas.stages.incorporated',
  },
  completed: {
    icon: CheckCircle2,
    color: 'text-primary-600 dark:text-primary-400',
    bg: 'bg-primary-500/10',
    label: 'empresas.stages.incorporated',
  },
  dissolved: {
    icon: AlertCircle,
    color: 'text-red-600 dark:text-red-400',
    bg: 'bg-red-500/10',
    label: 'empresas.stages.dissolved',
  },
  filed: {
    icon: FileText,
    color: 'text-secondary-500 dark:text-secondary-400',
    bg: 'bg-secondary-500/10',
    label: 'empresas.stages.filed',
  },
  filing: {
    icon: Clock,
    color: 'text-amber-600 dark:text-amber-400',
    bg: 'bg-amber-500/10',
    label: 'empresas.stages.inFormation',
  },
  details_pending: {
    icon: Clock,
    color: 'text-secondary-500 dark:text-secondary-400',
    bg: 'bg-secondary-500/10',
    label: 'empresas.stages.detailsPending',
  },
  under_review: {
    icon: Clock,
    color: 'text-secondary-500 dark:text-secondary-400',
    bg: 'bg-secondary-500/10',
    label: 'empresas.stages.underReview',
  },
  approved: {
    icon: CheckCircle2,
    color: 'text-primary-600 dark:text-primary-400',
    bg: 'bg-primary-500/10',
    label: 'import.statusSection.status.approved',
  },
  payment_completed: {
    icon: Clock,
    color: 'text-secondary-500 dark:text-secondary-400',
    bg: 'bg-secondary-500/10',
    label: 'empresas.stages.paymentCompleted',
  },
  payment_pending: {
    icon: AlertCircle,
    color: 'text-orange-600 dark:text-orange-400',
    bg: 'bg-orange-500/10',
    label: 'empresas.stages.paymentPending',
  },
  default: {
    icon: Clock,
    color: 'text-neutral-500 dark:text-neutral-400',
    bg: 'bg-neutral-500/10',
    label: 'empresas.stages.inProcess',
  },
};

function getStatusConfig(status, dissolutionStatus) {
  if ((dissolutionStatus || '').toLowerCase() === 'completed') {
    return STATUS_CONFIG.dissolved;
  }
  const normalizedStatus = status?.toLowerCase?.() || '';
  return STATUS_CONFIG[normalizedStatus] || STATUS_CONFIG.default;
}

// Company initial for avatar
function getCompanyInitial(name) {
  if (!name) return '?';
  return name.charAt(0).toUpperCase();
}

export default function CompanySelector({ collapsed = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    companies,
    selectedCompany,
    setSelectedCompany,
    isLoading,
    hasCompanies,
  } = useCompany();

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Filter companies by search
  const filteredCompanies = companies.filter((company) =>
    company.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setSearchQuery('');
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Focus search input when opening
  useEffect(() => {
    if (isOpen && searchInputRef.current && companies.length > 3) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen, companies.length]);

  // Handle company selection - stay on current page, just switch company
  const handleSelectCompany = (company) => {
    setSelectedCompany(company.id);
    setIsOpen(false);
    setSearchQuery('');

    // Check if current path is a company-scoped route (e.g., /empresa/{companyId}/dashboard)
    // Pattern: /empresa/{uuid-or-id}/{subpath}
    const companyRoutePattern =
      /^\/empresa\/([^/]+)\/(dashboard|notificaciones|documentos|banking|impuestos)(.*)$/;
    const match = location.pathname.match(companyRoutePattern);

    if (match) {
      // Replace the old companyId with the new one, keeping the same subpath
      const subpath = match[2]; // dashboard, documentos, etc.
      const rest = match[3] || ''; // any additional path segments
      navigate(`/empresa/${company.id}/${subpath}${rest}`);
    } else {
      // Not on a company-scoped route, navigate to dashboard
      navigate(ROUTES.COMPANY_DASHBOARD(company.id));
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchQuery('');
    }
  };

  // Get dropdown position
  const getDropdownPosition = () => {
    if (!triggerRef.current) return {};
    const rect = triggerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    // Calculate width based on sidebar state and viewport
    let width = collapsed ? Math.min(320, viewportWidth - 32) : rect.width;

    // Calculate left position
    let left = collapsed ? rect.right + 8 : rect.left;

    // Ensure dropdown doesn't go off-screen
    if (left + width > viewportWidth - 16) {
      left = viewportWidth - width - 16;
    }

    return {
      top: rect.top,
      left,
      width,
      maxHeight: `calc(100vh - ${rect.top + 16}px)`,
    };
  };

  // Loading state
  if (isLoading) {
    return (
      <div
        className={cn(
          'flex items-center justify-center rounded-lg',
          'bg-neutral-100/80 dark:bg-neutral-800/50',
          collapsed ? 'mx-auto h-11 w-11' : 'mx-auto h-12 w-[calc(100%-1.5rem)]'
        )}
      >
        <Loader2 className="h-4 w-4 animate-spin text-neutral-400" />
      </div>
    );
  }

  // No companies state
  if (!hasCompanies) {
    return (
      <div
        className={cn(
          'flex items-center rounded-lg',
          'border border-dashed border-neutral-300 dark:border-neutral-700',
          'text-neutral-500 dark:text-neutral-400',
          collapsed
            ? 'mx-auto h-11 w-11 justify-center'
            : 'mx-auto w-[calc(100%-1.5rem)] gap-2.5 px-2.5 py-2'
        )}
      >
        <Building2
          className={cn('flex-shrink-0', collapsed ? 'h-4 w-4' : 'h-5 w-5')}
        />
        {!collapsed && (
          <span className="text-[13px] font-medium">
            {t('sidebar.noCompanies')}
          </span>
        )}
      </div>
    );
  }

  const statusConfig = getStatusConfig(
    selectedCompany?.status,
    selectedCompany?.dissolutionStatus
  );
  const StatusIcon = statusConfig.icon;

  return (
    <>
      {/* Trigger Button */}
      <button
        ref={triggerRef}
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        className={cn(
          'group relative flex items-center',
          'rounded-lg transition-all duration-200',
          'border shadow-sm',
          isOpen
            ? 'border-primary-400 bg-primary-50/80 dark:border-primary-500 dark:bg-primary-900/20'
            : 'border-neutral-300 bg-white hover:border-primary-400 hover:bg-white dark:border-neutral-700 dark:bg-neutral-800/80 dark:hover:border-primary-600 dark:hover:bg-neutral-800',
          collapsed
            ? 'mx-auto h-11 w-11 justify-center'
            : 'mx-auto w-[calc(100%-1.5rem)] gap-2.5 px-2.5 py-2'
        )}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        title={collapsed ? selectedCompany?.name : undefined}
      >
        {/* Company Avatar */}
        <div
          className={cn(
            'flex flex-shrink-0 items-center justify-center rounded-md font-semibold',
            'bg-gradient-to-br from-primary-500 to-primary-600',
            'text-white shadow-sm',
            collapsed ? 'h-7 w-7 text-xs' : 'h-8 w-8 text-sm'
          )}
        >
          {getCompanyInitial(selectedCompany?.name)}
        </div>

        {/* Company Info */}
        {!collapsed && (
          <>
            <div className="min-w-0 flex-1 text-left">
              <p className="truncate text-[13px] font-semibold text-neutral-950 dark:text-white">
                {selectedCompany?.name}
              </p>
              <div className="flex items-center gap-1">
                <StatusIcon className={cn('h-3 w-3', statusConfig.color)} />
                <span
                  className={cn('text-[11px] font-medium', statusConfig.color)}
                >
                  {t(statusConfig.label)}
                </span>
              </div>
            </div>

            {/* Chevron */}
            <ChevronDown
              className={cn(
                'h-4 w-4 flex-shrink-0 text-neutral-600 transition-transform duration-200',
                'group-hover:text-primary-500 dark:group-hover:text-primary-400',
                isOpen && 'rotate-180'
              )}
            />
          </>
        )}
      </button>

      {/* Dropdown Portal */}
      {isOpen &&
        createPortal(
          <AnimatePresence>
            <motion.div
              ref={dropdownRef}
              initial={{
                opacity: 0,
                x: collapsed ? -8 : 0,
                y: collapsed ? 0 : -8,
                scale: 0.96,
              }}
              animate={{ opacity: 1, x: 0, y: 0, scale: 1 }}
              exit={{
                opacity: 0,
                x: collapsed ? -8 : 0,
                y: collapsed ? 0 : -8,
                scale: 0.96,
              }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
              style={getDropdownPosition()}
              className={cn(
                'fixed z-[9999] flex flex-col overflow-hidden rounded-xl',
                'border border-neutral-200 dark:border-neutral-700',
                'bg-white shadow-2xl dark:bg-neutral-900',
                'shadow-neutral-900/10 dark:shadow-neutral-950/50'
              )}
              role="listbox"
              onKeyDown={handleKeyDown}
            >
              {/* Header */}
              <div className="border-b border-neutral-200 bg-neutral-50 px-4 py-3 dark:border-neutral-800 dark:bg-neutral-800/50">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                  {t('sidebar.selectCompany')}
                </h3>
              </div>

              {/* Search - Only show if more than 3 companies */}
              {companies.length > 3 && (
                <div className="border-b border-neutral-100 p-2 dark:border-neutral-800">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={t('sidebar.searchCompanies')}
                      className={cn(
                        'w-full rounded-lg py-2.5 pl-10 pr-3 text-sm',
                        'bg-white dark:bg-neutral-800',
                        'text-neutral-900 dark:text-white',
                        'placeholder:text-neutral-400',
                        'border border-neutral-200 dark:border-neutral-700',
                        'focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/20'
                      )}
                    />
                  </div>
                </div>
              )}

              {/* Companies List */}
              <div
                className="custom-scrollbar flex-1 overflow-y-auto p-2"
                style={{ maxHeight: '320px' }}
              >
                {filteredCompanies.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-neutral-400">
                    <Search className="mb-2 h-8 w-8" />
                    <span className="text-sm">{t('noResults')}</span>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {filteredCompanies.map((company) => {
                      const isSelected = company.id === selectedCompany?.id;
                        const companyStatus = getStatusConfig(
                          company.status,
                          company.dissolutionStatus
                        );
                      const CompanyStatusIcon = companyStatus.icon;

                      return (
                        <button
                          key={company.id}
                          onClick={() => handleSelectCompany(company)}
                          className={cn(
                            'flex w-full items-center gap-3 rounded-xl px-3 py-3',
                            'transition-all duration-150',
                            isSelected
                              ? 'bg-primary-50 ring-1 ring-primary-200 dark:bg-primary-900/30 dark:ring-primary-700'
                              : 'hover:bg-neutral-100 dark:hover:bg-neutral-800'
                          )}
                          role="option"
                          aria-selected={isSelected}
                        >
                          {/* Company Avatar */}
                          <div
                            className={cn(
                              'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg text-sm font-bold',
                              isSelected
                                ? 'bg-gradient-to-br from-primary-500 to-primary-700 text-white shadow-md shadow-primary-500/25'
                                : 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300'
                            )}
                          >
                            {getCompanyInitial(company.name)}
                          </div>

                          {/* Company Info */}
                          <div className="min-w-0 flex-1 text-left">
                            <p
                              className={cn(
                                'truncate text-sm font-semibold',
                                isSelected
                                  ? 'text-primary-700 dark:text-primary-300'
                                  : 'text-neutral-900 dark:text-white'
                              )}
                            >
                              {company.name}
                            </p>
                            <div className="mt-0.5 flex items-center gap-1.5">
                              <CompanyStatusIcon
                                className={cn(
                                  'h-3.5 w-3.5',
                                  companyStatus.color
                                )}
                              />
                              <span
                                className={cn(
                                  'text-xs font-medium',
                                  companyStatus.color
                                )}
                              >
                                {t(companyStatus.label)}
                              </span>
                            </div>
                          </div>

                          {/* Selected Indicator */}
                          {isSelected && (
                            <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary-500 shadow-sm">
                              <CheckCircle2 className="h-4 w-4 text-white" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </motion.div>
          </AnimatePresence>,
          document.body
        )}
    </>
  );
}
